// Persistent cache of the featured wall's restorable state, so returning to the
// wall (browser back, or even after a full reload) can rebuild the ENTIRE list +
// scroll position immediately — without waiting on the snapshot API.
//
// Two keys, split by weight:
//   - LIST_KEY   : the heavy payload (snapshot + every loaded page). Written only
//                  when the list actually changes (page load / infinite scroll /
//                  filter change), never on scroll.
//   - SCROLL_KEY : just the scroll offset. Written frequently (on scroll), so it
//                  MUST stay cheap — a bare number, never the list.
const LIST_KEY = 'pr.featured.cache.v1';
const SCROLL_KEY = 'pr.featured.scrollY';
const ANCHOR_KEY = 'pr.featured.anchor';

// In-memory mirror — the source of truth mid-session (survives SPA nav) and a
// parse-cache so reads are free. `undefined` = not yet loaded from storage.
let listMem;

function readList() {
  if (listMem !== undefined) return listMem;
  try { listMem = JSON.parse(sessionStorage.getItem(LIST_KEY) || 'null'); } catch { listMem = null; }
  return listMem;
}

function writeList(next) {
  listMem = next;
  // The list can be large; if we blow the sessionStorage quota just keep the
  // in-memory copy (still restores across SPA nav, just not a full reload).
  try { sessionStorage.setItem(LIST_KEY, JSON.stringify(next)); } catch { /* quota / disabled */ }
}

/** The cached { snapshot, events, skip, hasMore, fundingFilter } (or null). */
export function getWallCache() { return readList(); }

/** Cache the snapshot (featured strip + all-events for city options). */
export function saveWallSnapshot(snapshot) {
  writeList({ ...(readList() || {}), snapshot });
}

/**
 * Cache the loaded pages (or any subset of them) so the exact list can be rebuilt
 * on return. Accepts a partial patch — e.g. { events, skip, hasMore } from the
 * fetch path, or { fundingFilter } on its own — merged over what's already cached.
 */
export function saveWallList(patch) {
  writeList({ ...(readList() || {}), ...patch });
}

/** Cache the scroll offset (cheap; written on every scroll). */
export function saveWallScroll(y) {
  try { sessionStorage.setItem(SCROLL_KEY, String(Math.round(y))); } catch { /* disabled */ }
}

/** The last saved scroll offset (0 if none). */
export function getWallScroll() {
  try { return parseInt(sessionStorage.getItem(SCROLL_KEY) || '0', 10) || 0; } catch { return 0; }
}

/**
 * The card at the top of the screen and how far down the viewport it sat — the precise half of
 * a restore. A bare scrollY lands on different content whenever anything above the list loads
 * at a different height than it did; the anchor re-pins the card itself. Cheap, like the offset.
 */
export function saveWallAnchor(anchor) {
  try { sessionStorage.setItem(ANCHOR_KEY, anchor ? JSON.stringify(anchor) : ''); } catch { /* disabled */ }
}

/** { id, top } or null. */
export function getWallAnchor() {
  try { return JSON.parse(sessionStorage.getItem(ANCHOR_KEY) || 'null'); } catch { return null; }
}
